import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import {
  useClassrooms,
  useCuratedAssignments,
  usePinAssignment,
  useStandardsTaxonomy,
} from '../teacher/useTeacher';
import './LessonLibrary.css';

type Kind = 'image' | 'audio' | 'pose';

/**
 * Lesson library — `/teacher/lessons`.
 *
 * Browse the curated catalogue. Filters are AND-combined (kind +
 * standards + free-text), matching the backend semantics. Picking a
 * unit opens the assignment detail; from there a teacher can pin it
 * to a classroom.
 *
 * The page also supports a `?pin-to=:classroomId` URL — when set,
 * each card has a quick "Pin here" button so a teacher coming from
 * a classroom doesn't have to click through to the detail page.
 */
export function LessonLibrary(): JSX.Element {
  const { data: user, isPending: userPending } = useCurrentUser();
  const [search, setSearch] = useSearchParams();
  const taxonomy = useStandardsTaxonomy();
  const classrooms = useClassrooms();

  const kind = (search.get('kind') ?? '') as Kind | '';
  const standards = useMemo(() => {
    const raw = search.get('standards');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [search]);
  const q = search.get('q') ?? '';
  const pinTo = search.get('pin-to') ?? '';

  const args: { kind?: Kind; standards?: string[]; q?: string } = {};
  if (kind) args.kind = kind;
  if (standards.length) args.standards = standards;
  if (q) args.q = q;
  const list = useCuratedAssignments(args);

  if (userPending) {
    return <div className="bl-lib" data-testid="lib-loading" />;
  }
  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="bl-lib">
        <p className="bl-lib__notice">Sign in as a teacher to browse the lesson library.</p>
      </div>
    );
  }

  const setKind = (next: Kind | ''): void => {
    const updated = new URLSearchParams(search);
    if (next) updated.set('kind', next);
    else updated.delete('kind');
    setSearch(updated);
  };

  const toggleStandard = (code: string): void => {
    const updated = new URLSearchParams(search);
    const current = standards.includes(code)
      ? standards.filter((c) => c !== code)
      : [...standards, code];
    if (current.length) updated.set('standards', current.join(','));
    else updated.delete('standards');
    setSearch(updated);
  };

  const setQuery = (next: string): void => {
    const updated = new URLSearchParams(search);
    if (next) updated.set('q', next);
    else updated.delete('q');
    setSearch(updated);
  };

  return (
    <div className="bl-lib">
      <header className="bl-lib__header">
        <Link to={pinTo ? `/teacher/classrooms/${pinTo}` : '/teacher'} className="bl-lib__back">
          {pinTo ? '← Back to classroom' : '← Your classrooms'}
        </Link>
        <h1 className="bl-lib__title">Lesson library</h1>
        <p className="bl-lib__subtitle">
          Curated units shipped with the product. Each one comes with a brief, rubric, and debrief
          questions — ready for next Monday.
        </p>
      </header>

      <div className="bl-lib__filters">
        <div className="bl-lib__filter-group">
          <span className="bl-lib__filter-label">Kind</span>
          <FilterChip active={kind === ''} onClick={() => setKind('')} label="All" />
          <FilterChip active={kind === 'image'} onClick={() => setKind('image')} label="Image" />
          <FilterChip active={kind === 'audio'} onClick={() => setKind('audio')} label="Audio" />
          <FilterChip active={kind === 'pose'} onClick={() => setKind('pose')} label="Pose" />
        </div>

        <div className="bl-lib__search">
          <input
            type="search"
            placeholder="Search briefs… (e.g. bias)"
            value={q}
            onChange={(e) => setQuery(e.target.value)}
            className="bl-lib__search-input"
            aria-label="Search assignments"
          />
        </div>
      </div>

      {taxonomy.data ? (
        <details className="bl-lib__standards-block">
          <summary className="bl-lib__standards-summary">
            Standards {standards.length ? `(${standards.length})` : ''}
          </summary>
          <div className="bl-lib__standards-bodies">
            {taxonomy.data.bodies.map((body) => (
              <div key={body.code} className="bl-lib__standards-body">
                <h3 className="bl-lib__standards-body-name">{body.name}</h3>
                <p className="bl-lib__standards-body-desc">{body.description}</p>
                <div className="bl-lib__standards-tags">
                  {(taxonomy.data?.tags_by_body[body.code] ?? []).map((tag) => (
                    <FilterChip
                      key={tag.code}
                      active={standards.includes(tag.code)}
                      onClick={() => toggleStandard(tag.code)}
                      label={tag.label}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {list.isPending ? (
        <p className="bl-lib__notice">Loading the catalogue…</p>
      ) : list.isError ? (
        <p className="bl-lib__notice">
          Couldn&apos;t load the catalogue. Try clearing the filters.
        </p>
      ) : !list.data || list.data.length === 0 ? (
        <div className="bl-lib__empty">
          <p>No units match these filters.</p>
          <p className="bl-lib__empty-hint">Try widening the standards selection.</p>
        </div>
      ) : (
        <ul className="bl-lib__cards">
          {list.data.map((a) => (
            <li key={a.id} className="bl-lib__card">
              <div className="bl-lib__card-head">
                <span className={`bl-lib__kind-badge bl-lib__kind-badge--${a.project_kind}`}>
                  {a.project_kind}
                </span>
                {a.time_estimate_minutes ? (
                  <span className="bl-lib__time">~{a.time_estimate_minutes} min</span>
                ) : null}
              </div>
              <h3 className="bl-lib__card-name">{a.name}</h3>
              {a.standards_tags.length ? (
                <p className="bl-lib__card-tags">
                  {a.standards_tags
                    .map((code) => {
                      // Find the human label from taxonomy for compactness.
                      if (!taxonomy.data) return code;
                      for (const body of Object.keys(taxonomy.data.tags_by_body)) {
                        const t = (taxonomy.data.tags_by_body[body] ?? []).find(
                          (x) => x.code === code,
                        );
                        if (t) return t.label;
                      }
                      return code;
                    })
                    .slice(0, 4)
                    .join(' · ')}
                </p>
              ) : null}
              <div className="bl-lib__card-actions">
                <Link
                  to={`/teacher/lessons/${a.id}${pinTo ? `?pin-to=${pinTo}` : ''}`}
                  className="bl-lib__card-link"
                >
                  Open →
                </Link>
                {pinTo && classrooms.data ? (
                  <PinButton
                    classroomId={pinTo}
                    assignmentId={a.id}
                    classroomName={classrooms.data.find((c) => c.id === pinTo)?.name ?? null}
                  />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterChip(props: { active: boolean; onClick: () => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      className={props.active ? 'bl-lib__chip bl-lib__chip--active' : 'bl-lib__chip'}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

function PinButton({
  classroomId,
  assignmentId,
  classroomName,
}: {
  classroomId: string;
  assignmentId: string;
  classroomName: string | null;
}): JSX.Element {
  const pin = usePinAssignment(classroomId);
  return (
    <button
      type="button"
      className="bl-lib__pin-btn"
      onClick={() => pin.mutate(assignmentId)}
      disabled={pin.isPending || pin.isSuccess}
    >
      {pin.isSuccess
        ? `Pinned to ${classroomName ?? 'classroom'}`
        : pin.isPending
          ? 'Pinning…'
          : `Pin to ${classroomName ?? 'classroom'}`}
    </button>
  );
}
